import {
    Box,
    Button,
    Flex,
    Image,
    Text,
    Tabs,
    Tab,
    TabList,
    Tooltip,
    useDisclosure,
  } from '@chakra-ui/react';
  import { useState, useRef, useEffect } from 'react';
  import { FaExchangeAlt } from 'react-icons/fa';
  import { ChevronRightIcon } from '@chakra-ui/icons';
  import { truncateString } from 'utils/functions';
import ReplaceDriveModal from 'components/drive/ReplaceDriveModal';
  
  const ReplaceComponent = ({
    mediaType,
    setselectedMediaUrls,
    selectedMediaUrls = [],
    setMediaFiles,
    onClose,
    setActiveStep
  }) => {

    const { isOpen: isReplaceModalOpen, onOpen: OnReplaceModalOpen, onClose: OnReplaceModalClose } = useDisclosure();
    // Extract unique ad accounts info (id + name)
    const adAccounts = selectedMediaUrls.map((group) => ({
      id: group[0]?.adsAccountId,
      name: group[0]?.adsAccountName || 'Unknown Account',
    }));

    // Remove duplicates by id
    const uniqueAdAccounts = Array.from(
      new Map(adAccounts.map((acc) => [acc.id, acc])).values()
    );
  
    // Keep track of active tab index to sync with activeAccountId
    const [activeTabIndex, setActiveTabIndex] = useState(0);
    // Map from account id to selected set index
    const [activeSetIndexByAccount, setActiveSetIndexByAccount] = useState({});
    const [replacingIndex, setReplacingIndex] = useState({ groupIndex: null, placementIndex: null });
  
    // Update activeAccountId from activeTabIndex
    const activeAccountId = uniqueAdAccounts[activeTabIndex]?.id;
  
    // Filter groups for current active account
    const setsForActiveAccount = selectedMediaUrls
      .map((group, originalIndex) => ({
        group,
        originalIndex,
      }))
      .filter(({ group }) => group[0]?.adsAccountId === activeAccountId);
  
    // Current set index for active account (default 0)
    const currentSetIndex = activeSetIndexByAccount[activeAccountId] ?? 0;
  
    // Ref for TabList to check scroll width
    const tabListRef = useRef(null);
    // State to track overflow
    const [isOverflowing, setIsOverflowing] = useState(false);
  
    // Effect to detect overflow
    useEffect(() => {
      const el = tabListRef.current;
      if (!el) return;
  
      const checkOverflow = () => {
        setIsOverflowing(el.scrollWidth > el.clientWidth);
      };
  
      checkOverflow();
  
      // Optionally listen to resize events to re-check overflow
      window.addEventListener('resize', checkOverflow);
      return () => {
        window.removeEventListener('resize', checkOverflow);
      };
    }, [uniqueAdAccounts]);

    const handleReplaceImage = (groupIndex, placementIndex) => {
        setReplacingIndex({ groupIndex, placementIndex });
        OnReplaceModalOpen();
    };

    const handleImageSelectedFromReplaceDrive = (file) => {
      if (file && file.length > 0) {
        const newImage = file[0];
    
        // --- Update selectedMediaUrls ---
        setselectedMediaUrls((prev) => {
          const updated = prev.map((group, groupIdx) => {

            if (groupIdx !== replacingIndex.groupIndex) return group;

            return group.map((placementItem, placementIdx) => {
              if (placementIdx !== replacingIndex.placementIndex) return placementItem;
              return {
                ...placementItem,
                url: newImage?.url || newImage?.picture,
                hash: newImage?.creativeId,
                mediaHash: newImage?.hash,
                name: newImage?.name,
                source: newImage?.source,
              };
            });
          });
          return updated;
        });
    
        // --- Update mediaFiles ---
        setMediaFiles((prev) => {
          const updated = prev.map((media, index) => {
            if (index !== replacingIndex.groupIndex) return media;
    
            return {
              ...newImage
            };
          });

          return updated;
        });
      }
    
      setReplacingIndex(null);
      OnReplaceModalClose();
    };
    
  
    return (
        <>
        <ReplaceDriveModal
          isDriveModalOpen={isReplaceModalOpen}
          selectedAdAccounts={[
            {
              adsAccountId: uniqueAdAccounts[activeTabIndex]?.id,
              adsAccountName: uniqueAdAccounts[activeTabIndex]?.name,
            }
          ]}
          onClose={() => {
            OnReplaceModalClose();
          }}
          onSubmit={handleImageSelectedFromReplaceDrive}
          uploadPurpose="update"
          mediaType={mediaType}
        />

        <Box width="100%">
        {/* Ad Account Tabs */}
        <Tabs
          index={activeTabIndex}
          onChange={(index) => {
            setActiveTabIndex(index);
            // Optionally reset set index for new account or keep as is
            // setActiveSetIndexByAccount(prev => ({ ...prev, [uniqueAdAccounts[index].id]: 0 }));
          }}
          colorScheme="brand"
          isLazy
        >
          <Flex position="relative" align="center">
            <TabList
              ref={tabListRef}
              overflowX="auto"
              whiteSpace="nowrap"
              gap={2}
              width="65vw"
              pr={isOverflowing ? '25px' : '0'} // leave space for arrow if overflowing
              css={{
                /* Hide scrollbar but keep scrolling */
                scrollbarWidth: 'none', 
                msOverflowStyle: 'none', 
                '&::-webkit-scrollbar': {
                  display: 'none',
                },
              }}
            >
              {uniqueAdAccounts.map(({ id, name }) => (
                <Tooltip key={id} label={name} isDisabled={name.length <= 15}>
                  <Tab
                    fontSize="14px"
                    whiteSpace="nowrap"
                    _selected={{ borderBottom: '2px solid', color: 'brand.500' }}
                  >
                    {truncateString(name, 15)}
                  </Tab>
                </Tooltip>
              ))}
            </TabList>
  
            {isOverflowing && (
              <Box
                position="absolute"
                right={0}
                bg="white"
                pl={2}
                pointerEvents="none"
              >
                <ChevronRightIcon boxSize={5} />
              </Box>
            )}
          </Flex>
        </Tabs>
  
        {/* Now render sets thumbnails and media grid as usual */}
        <Box my={4} overflowX="auto" width="65vw">
          <Flex gap={2} wrap="nowrap" minWidth="max-content">
            {setsForActiveAccount.map(({ group }, filteredIndex) => {
              const previewItem = group.find((item) => item.placement === 'square') || group[0];
              const isActive = filteredIndex === currentSetIndex;
              const thumbnail = previewItem?.source || previewItem?.url;
  
              return (
                <Box
                  key={filteredIndex}
                  border={isActive ? '2px solid #8671cc' : '1px solid #ccc'}
                  borderRadius="md"
                  cursor="pointer"
                  p={1}
                  onClick={() =>
                    setActiveSetIndexByAccount((prev) => ({
                      ...prev,
                      [activeAccountId]: filteredIndex,
                    }))
                  }
                  flexShrink={0}
                >
                  {previewItem.source ? (
                    <Box
                      as="video"
                      src={previewItem.source}
                      width="60px"
                      height="60px"
                      autoPlay
                      muted
                      loop
                    />
                  ) : (
                    <Image
                      src={thumbnail}
                      width="60px"
                      height="60px"
                      objectFit="cover"
                    />
                  )}
                </Box>
              );
            })}
          </Flex>
        </Box>

        {/* Media Grid */}
        <Box maxH={{base: '500px', md: '460px'}} overflowY="auto">
          <Flex
            wrap="wrap"
            gap={10}
            justifyContent={'center'}
          >
            {setsForActiveAccount[currentSetIndex]?.group?.map(
              ({ placement, url, source }, placementIndex) => {
                const originalGroupIndex =
                  setsForActiveAccount[currentSetIndex]?.originalIndex;

                return (
                  <Box
                    key={`${activeAccountId}-${originalGroupIndex}-${placement}`}
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="md"
                    p={2}
                    width="240px"
                    height={
                      placement === 'square'
                        ? '315px'
                        : placement === 'vertical'
                        ? '530px'
                        : '235px'
                    }
                    display="flex"
                    flexDirection="column"
                    gap={4}
                  >
                    <Text fontWeight="bold" textTransform="capitalize">
                      {placement}
                    </Text>

                    <Box
                      width="220px"
                      height={
                        placement === 'square'
                          ? '200px'
                          : placement === 'vertical'
                          ? '440px'
                          : '120px'
                      }
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      bg="#BDBDBD"
                      borderRadius="md"
                    >
                      {source ? (
                        <Box
                          as="video"
                          controls
                          src={source}
                          maxWidth="100%"
                          maxHeight="100%"
                          style={{ objectFit: 'contain' }}
                        />
                      ) : (
                        <Image
                          src={url}
                          alt={placement}
                          maxWidth="100%"
                          maxHeight="100%"
                          objectFit="contain"
                          objectPosition="center"
                        />
                      )}
                    </Box>

                    <Flex>
                      <Button
                        size="sm"
                        variant={'solid'}
                        colorScheme={'gray'}
                        mt={2}
                        leftIcon={<FaExchangeAlt />}
                        onClick={() =>
                          handleReplaceImage(originalGroupIndex, placementIndex)
                        }
                      >
                        Replace
                      </Button>
                    </Flex>
                  </Box>
                );
              }
            )}
          </Flex>
        </Box>


          <Flex gap={2} justifyContent={'flex-end'}>
            <Button variant={'solid'} colorScheme='gray' onClick={onClose} size={'sm'}>
                Close
            </Button>
            <Button
                colorScheme={'brand'}
                size={'sm'}
                  onClick={() => {
                      onClose();
                      setActiveStep('Media');
                  }
                }
            >Done</Button>
          </Flex>
          
        </Box>
    </>
    );
  };
  
  export default ReplaceComponent;
  